/**
 * The repeated compounds, as components rather than as CSS classes.
 *
 * This is the file that keeps the Tailwind honest. A button appears on nine screens; if
 * its utility string is typed out nine times then the ninth is subtly wrong within a
 * week and nobody notices, because a slightly-wrong button still looks like a button.
 * Here it is one string, the variants are a union type, and a caller physically cannot
 * invent a seventh kind.
 *
 * The rule for what belongs here: a thing with a NAME in the product's vocabulary - a
 * button, a chip, a card, a badge. Layout and one-off spacing stay as utilities in the
 * JSX where they are read, because that is the half of Tailwind worth having.
 */

import { useId } from "react";
import { Link } from "react-router-dom";
import type { ComponentProps, ReactNode } from "react";
import { Info, Tick } from "./components/Icons";

/** Joins class strings, dropping the falsy ones. */
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/* --- buttons ------------------------------------------------------------------------- */

type Variant = "default" | "primary" | "danger" | "ghost";

const BUTTON_BASE =
  "inline-flex items-center gap-[8px] whitespace-nowrap rounded-sm px-[15px] py-[8px] " +
  "text-sm font-medium transition-colors disabled:opacity-55 disabled:cursor-default";

/*
 * THE SAME BUTTON FOR A THUMB, as a SECOND COMPLETE STRING rather than a `className` that
 * "overrides" the first. `h-14 text-xl` appended to `py-[8px] text-sm` is two rules of equal
 * specificity per property, and which one wins is decided by the order the stylesheet emits
 * them - the trap the note on `CONTROL_SKIN` below describes. The simplified call register
 * is read at a counter, and its buttons are 56px tall because a finger in a work glove is.
 */
const BUTTON_BASE_LARGE =
  "inline-flex h-14 items-center justify-center gap-3 whitespace-nowrap rounded-md px-6 " +
  "text-xl font-semibold transition-colors disabled:opacity-55 disabled:cursor-default";

/* The large default keeps a 2px edge: at that size a hairline disappears into the ground. */
const LARGE_EDGE = "border-2";

/*
 * THE PRIMARY BUTTON IS INK, NOT THE ACCENT, and that is a decision about what indigo is
 * for rather than about what looks good. Chrome is neutral in this product now: the thing
 * you are meant to press is the darkest object on the screen, and indigo is reserved for
 * references - a link, a focus ring, a switch that is on. A filled indigo button put the
 * accent at a size where it competed with the charts, which is the one thing the two
 * colour systems in `styles.css` are not allowed to do.
 *
 * The hover is `ink-soft` rather than a dedicated step: white on it is 8.6:1, it is
 * already the second rung of the ramp, and a button that lightens on hover reads as
 * pressable in the same way a link that darkens does.
 */
const BUTTON_VARIANTS: Record<Variant, string> = {
  default:
    "bg-paper text-ink border border-line hover:border-line-strong hover:bg-sunken " +
    "[&_svg]:text-ink-faint hover:[&_svg]:text-ink-soft",
  primary: "bg-ink text-paper border border-ink hover:bg-ink-soft hover:border-ink-soft",
  danger:
    "bg-paper text-alert border border-alert-line hover:bg-alert-soft hover:border-alert",
  ghost: "bg-transparent text-ink border border-transparent hover:bg-line-soft",
};

export function buttonClass(
  variant: Variant = "default",
  extra?: string,
  size: "normal" | "large" = "normal",
): string {
  if (size === "large") {
    // `border` in the variant and `border-2` here are one width each, never both: the
    // variant's is swapped out rather than out-ranked.
    const skin = BUTTON_VARIANTS[variant].replace(/(^| )border( |$)/, `$1${LARGE_EDGE}$2`);
    return cx(BUTTON_BASE_LARGE, skin, extra);
  }
  return cx(BUTTON_BASE, BUTTON_VARIANTS[variant], extra);
}

export function Button({
  variant = "default",
  size = "normal",
  className,
  ...rest
}: ComponentProps<"button"> & { variant?: Variant; size?: "normal" | "large" }) {
  return <button className={buttonClass(variant, className, size)} {...rest} />;
}

/** The same thing, as a link. `tel:` and `mailto:` are actions too. */
export function ButtonLink({
  variant = "default",
  className,
  ...rest
}: ComponentProps<"a"> & { variant?: Variant }) {
  return <a className={buttonClass(variant, className)} {...rest} />;
}

export function ButtonTo({
  variant = "default",
  className,
  ...rest
}: ComponentProps<typeof Link> & { variant?: Variant }) {
  return <Link className={buttonClass(variant, className)} {...rest} />;
}

/** A square button that is only an icon. The label goes in `title` and in an sr-only. */
export function IconButton({ className, ...rest }: ComponentProps<"button">) {
  return (
    <button
      className={cx(
        "grid size-7 flex-none place-items-center rounded-sm text-ink-faint",
        "hover:bg-line hover:text-ink",
        className,
      )}
      {...rest}
    />
  );
}

/* --- surfaces ------------------------------------------------------------------------- */

/*
 * NO SHADOW. A drop shadow under every card would be the only thing on the screen with a
 * soft edge - and forty soft edges read as a stack of floating dialogs rather than as one
 * document. Two things do that work instead, and they agree with each other: the border,
 * and the fact that `paper` is lighter than the page it sits on.
 *
 * That second one is new. This note used to say the border did the whole job alone, which
 * is why `--color-line` was set a step stronger than a grey-ground design would need. The
 * ground has since moved off white, so the line is now doing less than it was built for.
 * It is deliberately unchanged for the moment; see the note in `styles.css`.
 */
const SURFACE = "bg-paper border border-line rounded-lg";

/**
 * A card with a name on it.
 *
 * The `h2` is a real heading rather than a styled div: these are the landmarks somebody
 * navigating by headings uses to skip through a call, and there are a dozen of them on
 * the detail page.
 */
export function Panel({
  title,
  icon,
  aside,
  className,
  children,
  bare,
}: {
  title?: ReactNode;
  icon?: ReactNode;
  aside?: ReactNode;
  className?: string;
  children: ReactNode;
  /** Drop the inner padding, for a panel whose content is a table or a ribbon. */
  bare?: boolean;
}) {
  return (
    <section className={cx(SURFACE, "mb-3.5", bare ? "overflow-hidden" : "px-4 pt-4 pb-4.5", className)}>
      {title && (
        <div
          className={cx(
            "flex flex-wrap items-center justify-between gap-3",
            bare ? "px-4 pt-4" : "mb-3",
          )}
        >
          <h2 className="flex items-center gap-2 text-base font-semibold text-ink">
            {icon && <span className="flex text-ink-faint">{icon}</span>}
            {title}
          </h2>
          {aside && (
            <div className="flex items-center gap-2.5 text-xs text-ink-faint">{aside}</div>
          )}
        </div>
      )}
      {children}
    </section>
  );
}

/** A surface with no padding and no heading, for a table that fills it. */
export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cx(SURFACE, "overflow-hidden", className)}>{children}</div>;
}

/* --- small type ------------------------------------------------------------------------ */

export function Chip({
  className,
  children,
  ...rest
}: ComponentProps<"span"> & { children: ReactNode }) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-[5px] whitespace-nowrap rounded-xs border border-line",
        "bg-sunken px-2 py-0.5 text-xs text-ink-soft",
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}

/** A chip that removes a filter. The only chip that is a button. */
export function RemovableChip({
  onRemove,
  children,
}: {
  onRemove: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onRemove}
      className={cx(
        "group inline-flex items-center gap-[5px] whitespace-nowrap rounded-xs",
        "border border-accent-line bg-accent-soft py-0.5 pr-[5px] pl-2 text-xs text-accent",
      )}
    >
      {children}
      <span
        aria-hidden="true"
        className={cx(
          "grid size-3.5 place-items-center rounded-full text-sm leading-none opacity-65",
          "group-hover:bg-accent group-hover:text-accent-ink group-hover:opacity-100",
        )}
      >
        ×
      </span>
      <span className="sr-only">Remove this filter</span>
    </button>
  );
}

/** The word that means somebody has to do something. */
export function Flag({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-[5px] text-2xs font-semibold tracking-[0.04em] text-alert uppercase">
      {children}
    </span>
  );
}

/**
 * An `i` beside a heading, and the sentence it is hiding.
 *
 * FOR THE THING THE SCREEN CANNOT SHOW YOU. A section's `note` says what a control is;
 * this says where its effect LANDS, which is a different question and one an owner only
 * asks once. Printing both under every heading would turn a settings page into a manual,
 * and the answer that matters most - "editing this moves a number on another page" - is
 * exactly the one that gets skipped when it is the fourth line of prose.
 *
 * NO JAVASCRIPT AND NO PORTAL. `group-hover` and `group-focus-within` are the whole of the
 * behaviour: the bubble is always in the DOM, so `aria-describedby` reaches it whether or
 * not it is painted, and a screen reader announces it as part of the button rather than
 * needing the pointer event that a tooltip built on state would wait for.
 *
 * IT DOES NOT FLOAT, because nothing in this product does (see the note in `styles.css`).
 * Its border and its ground separate it from the text underneath, exactly like the agent
 * menu and every other surface here.
 */
export function InfoTip({
  label = "What this affects",
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <span className="group relative inline-flex align-middle">
      <button
        type="button"
        // A BUTTON, not a bare icon with a `title`. `title` is unreachable from a keyboard,
        // invisible on a touch screen and unstyleable; this is focusable, so the sentence
        // is available to somebody who never touches a mouse.
        aria-label={label}
        aria-describedby={id}
        className={cx(
          "rounded-full text-ink-ghost transition-colors",
          "hover:text-ink-soft focus-visible:text-ink-soft",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
          "focus-visible:outline-accent",
        )}
      >
        <Info size={15} />
      </button>
      <span
        role="tooltip"
        id={id}
        className={cx(
          // Anchored to the icon's LEFT rather than centred on it. These sit at the end of
          // a heading in a 320px column, and a centred bubble hangs off the page on the
          // ones whose heading is short.
          "pointer-events-none absolute top-full left-0 z-20 mt-2 w-64",
          "rounded-md border border-line bg-paper px-3 py-2.5",
          "text-sm leading-snug font-normal text-ink-soft normal-case",
          "opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100",
        )}
      >
        {children}
      </span>
    </span>
  );
}

/** A monospace identifier, read character by character. */
export function Mono({ children }: { children: ReactNode }) {
  return (
    <code className="rounded-xs border border-line bg-sunken px-[6px] py-px font-mono text-[0.92em]">
      {children}
    </code>
  );
}

/* --- controls -------------------------------------------------------------------------- */

/*
 * THE SHAPE IS SPLIT OUT FROM THE SKIN, and that is a Tailwind fact rather than taste.
 *
 * `cx("rounded-sm", "rounded-full")` does NOT give you a pill. Both classes land in the
 * attribute, both have the same specificity, and the winner is whichever Tailwind emitted
 * LAST into the stylesheet - which for the radius scale is `rounded-sm`, because the sort
 * is by the theme's own order and `full` is not the end of it. A component that "overrides"
 * a base string by appending a second value of the same property is relying on an ordering
 * nobody wrote down and half the time gets the other one. So there are two complete strings
 * and a caller picks one, instead of one string and a patch.
 */
const CONTROL_SKIN = "border border-line bg-paper text-sm text-ink placeholder:text-ink-ghost";
const CONTROL = `rounded-sm px-2.5 py-[8px] ${CONTROL_SKIN}`;
/** The same box for a control that is not a native field: `Picker`'s trigger. */
export const CONTROL_BOX = CONTROL;
/** The same control, as the round-ended thing the filter bar wears. See `Select`'s note. */
const CONTROL_PILL = `rounded-full px-3.5 py-[7px] ${CONTROL_SKIN}`;
/**
 * And the same pill once it is doing something. A THIRD COMPLETE STRING for the same
 * reason there is a second one: `bg-accent-soft` appended after `bg-paper` is two
 * background rules of equal specificity, and the stylesheet's order decides it, not the
 * class attribute's. Written out, it is decided here.
 */
const CONTROL_PILL_SET =
  "rounded-full px-3.5 py-[7px] border border-accent-line bg-accent-soft text-sm text-ink";

export function TextInput({
  className,
  pill,
  ...rest
}: ComponentProps<"input"> & { pill?: boolean }) {
  return <input className={cx(pill ? CONTROL_PILL : CONTROL, className)} {...rest} />;
}

/**
 * The first `<textarea>` in the product.
 *
 * Same `CONTROL` string as `TextInput`, so the border, the focus ring and the placeholder
 * ink match every other control for free rather than being re-typed one shade off. The
 * only additions are the two things a multi-line control needs and a single-line one does
 * not: a leading that is readable over three lines, and a resize handle that cannot drag
 * the panel sideways.
 */
export function TextArea({ className, ...rest }: ComponentProps<"textarea">) {
  return <textarea className={cx(CONTROL, "resize-y leading-normal", className)} {...rest} />;
}

/**
 * `pill` IS A FILTER THAT SAYS WHETHER IT IS DOING ANYTHING, and that is the whole of it.
 *
 * A rank of four identical grey dropdowns is a bar you have to read left to right to find
 * out what you are looking at. Rounded, and tinted the moment a value is chosen, the set
 * answers "is anything filtered here" from across the room - which is the question a short
 * list gets asked most, and the same job the removable chips underneath do in words.
 *
 * `set` IS THE CALLER'S TO PASS. A `select` cannot tell its empty option from a chosen one:
 * `value=""` is a perfectly good value and this component has no idea which of its children
 * means "no filter". The page knows, because the page put it in the query string.
 *
 * IT IS STILL A NATIVE `select`. The prototype this came from drew these as buttons that
 * cycle to the next value on each click, which costs up to seven clicks to reach one of the
 * seven bands, cannot show the outcome list this database actually holds, and re-implements
 * the keyboard, the type-ahead and the mobile picker that the platform gives away.
 */
export function Select({
  className,
  children,
  pill,
  set,
  ...rest
}: ComponentProps<"select"> & { pill?: boolean; set?: boolean }) {
  return (
    <select
      className={cx(
        // Ink rather than `text-accent` on a set pill's word: the tint and the border are
        // already saying "this one is on", and a violet word inside a violet pill is the
        // same fact three times, at the cost of the one thing here anybody reads. Which is
        // also why only an UNSET filter pill is grey: its word is a placeholder, like an
        // input's. A plain select always shows a value somebody picked, and that is ink.
        pill ? (set ? CONTROL_PILL_SET : CONTROL_PILL) : CONTROL,
        "cursor-pointer pr-[26px] hover:border-line-strong",
        pill && !set && "text-ink-soft",
        // A disabled select has to LOOK unavailable, or it reads as a control that simply
        // refuses to open. This used to be justified by the opening-hours grid, which kept
        // a closed day's two times on screen and greyed them; that grid has fixed columns
        // now and says "After hours" instead, so nothing in the product currently disables
        // a select. The styling stays because it is what a disabled one should look like
        // whenever the next one appears, not because something needs it today.
        "disabled:cursor-default disabled:bg-sunken disabled:text-ink-ghost disabled:hover:border-line",
        className,
      )}
      {...rest}
    >
      {children}
    </select>
  );
}

/** A checkbox with its label, which is the only way this product uses one. */
export function Toggle({
  checked,
  onChange,
  children,
  className,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label
      className={cx(
        "inline-flex cursor-pointer items-center gap-[8px] text-sm font-medium",
        "text-ink select-none",
        className,
      )}
    >
      {/*
        THE REAL CHECKBOX IS STILL HERE, just not the thing you see. `appearance-none` on a
        native input keeps the whole control - the label association, the space bar, the
        `:focus-visible` ring, the accessibility tree - and hands back only the painting.
        Drawing the box on a `<span>` and hiding the input would mean re-teaching all four,
        and the third one silently: a ring that lands on a `sr-only` input is a ring nobody
        can see.

        The tick is laid OVER the input rather than inside it, because an input has no box
        to put a child in. The wrapper is what makes that a centring problem rather than a
        sum of margins that breaks the moment the gap changes.
      */}
      <span className="relative grid size-[18px] flex-none place-items-center">
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          className={cx(
            "peer col-start-1 row-start-1 size-full cursor-pointer appearance-none",
            "rounded-[5px] border-[1.5px] border-line-strong bg-paper transition-colors",
            "checked:border-accent checked:bg-accent",
          )}
        />
        <Tick
          size={11}
          strokeWidth={3}
          className={cx(
            "pointer-events-none col-start-1 row-start-1 text-paper",
            "opacity-0 peer-checked:opacity-100",
          )}
        />
      </span>
      {children}
    </label>
  );
}

/**
 * On or off, as a thing you flick.
 *
 * NOT A REPLACEMENT FOR `Toggle`, AND THE DIFFERENCE IS WHAT EACH ONE MEANS. A checkbox is
 * an option you tick on a form you then submit - "include test calls" is one. This is a
 * switch: seven of them in a column, each one the answer to "is this day open", where the
 * state of the row IS the setting and the word beside it changes with it.
 *
 * A `button role="switch"` rather than a styled checkbox, because that is the role whose
 * announcement is "on"/"off" rather than "checked", and `aria-checked` carries it. The
 * label is a string rather than children: the visible word next to it says Open or Closed,
 * which is the state and not the name of the thing being switched.
 */
export function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cx(
        "relative h-5 w-[34px] flex-none cursor-pointer rounded-full transition-colors",
        checked ? "bg-accent" : "bg-line-strong",
      )}
    >
      {/* No shadow under the knob: there are no shadow tokens in this product and nothing
          floats. A white circle on a filled track is legible without one. */}
      <span
        aria-hidden="true"
        className={cx(
          "absolute top-0.5 size-4 rounded-full bg-paper transition-[left]",
          checked ? "left-[16px]" : "left-0.5",
        )}
      />
    </button>
  );
}

/**
 * One choice out of three or four, shown all at once.
 *
 * Used for the date range and for the tabs on a call. A `select` would hide two of the
 * three options behind a click for no gain: the whole set fits.
 */
/*
 * THE LARGE ONE IS TWO MORE COMPLETE STRINGS, for the reason `BUTTON_BASE_LARGE` gives: a
 * `text-lg` appended to `text-sm` is decided by stylesheet order, not by the attribute. It
 * is the simplified call register's filter, pressed with a thumb at a counter, so each
 * option is at least 48px tall.
 *
 * EQUAL COLUMNS ACROSS THE FULL WIDTH, AND A LABEL WRAPS INSIDE ITS OWN BOX. The first cut
 * let the set wrap, and on a 1024px tablet the third option dropped onto a second row -
 * so the control was a different shape from the list it sat over, and pressing a tab moved
 * things. A fixed strip of equal cells keeps every option where it was; "Waiting on you"
 * going to two lines in a narrow list is the whole of the cost.
 */
const SEGMENTED_TRACK = "inline-flex rounded-md border border-line bg-paper p-[3px]";
const SEGMENTED_TRACK_LARGE =
  "grid w-full grid-flow-col auto-cols-fr gap-1 rounded-lg border-2 border-line bg-paper p-1";
const SEGMENTED_OPTION =
  "cursor-pointer rounded-[7px] px-3.5 py-[7px] text-sm font-medium whitespace-nowrap";
const SEGMENTED_OPTION_LARGE =
  "min-h-12 cursor-pointer rounded-md px-3 py-1.5 text-lg leading-tight font-semibold text-balance";

export function Segmented<T extends string | number>({
  value,
  options,
  onChange,
  label,
  size = "normal",
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (next: T) => void;
  label: string;
  size?: "normal" | "large";
}) {
  const large = size === "large";
  return (
    <div
      role="group"
      aria-label={label}
      className={large ? SEGMENTED_TRACK_LARGE : SEGMENTED_TRACK}
    >
      {options.map((option) => {
        const on = option.value === value;
        return (
          <button
            key={String(option.value)}
            // A plain button, never a submit: the dashboard view on /settings is the first of
            // these to sit inside a `<form>`, where a bare `<button>` defaults to submitting it.
            type="button"
            aria-pressed={on}
            onClick={() => onChange(option.value)}
            className={cx(
              large ? SEGMENTED_OPTION_LARGE : SEGMENTED_OPTION,
              // Ink rather than the accent, for the same reason the primary button is ink:
              // the selected range is a commitment and not a reference. White on it is
              // 18.95:1, which is why the pill needs no border of its own.
              on ? "bg-ink text-paper" : "text-ink hover:bg-sunken",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/* --- key/value ---------------------------------------------------------------------------- */

/**
 * The label/value grid used by every "here are the facts" block.
 *
 * `max-content` on the first column and `minmax(0,1fr)` on the second is what keeps a
 * long value wrapping inside its own column instead of widening the panel and pushing
 * the diagnostics table out of the card.
 */
export function Pairs({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <dl
      className={cx(
        "grid grid-cols-[minmax(88px,max-content)_minmax(0,1fr)] items-baseline gap-x-4 gap-y-[9px] text-sm",
        className,
      )}
    >
      {children}
    </dl>
  );
}

export function Pair({
  label,
  children,
  muted,
}: {
  label: ReactNode;
  children: ReactNode;
  /** A value the platform did not author - an agent's own field, or a missing one. */
  muted?: boolean;
}) {
  return (
    <>
      <dt className="text-sm text-ink-faint">{label}</dt>
      <dd
        className={cx(
          "m-0 [overflow-wrap:anywhere]",
          muted ? "text-ink-soft" : "font-medium",
          "[&_a]:text-accent hover:[&_a]:underline",
        )}
      >
        {children}
      </dd>
    </>
  );
}

/* --- tables --------------------------------------------------------------------------------- */

/**
 * The base every table wears.
 *
 * THE LAST-ROW RULE IS ADDRESSED FROM THE TABLE, NOT FROM THE CELL. It was on `TD` as
 * `last:border-b-0`, which is `:last-child` - the last CELL of every row, not the last
 * row - so each separator stopped short of the right-hand column and every table on
 * every screen had a notch cut out of the right end of every rule.
 * `tbody tr:last-child > td` is the thing that was meant.
 */
const TABLE_BASE = "border-collapse [&_tbody_tr:last-child>td]:border-b-0";

export const TABLE = `w-full ${TABLE_BASE}`;

/**
 * The same table, pulled out to a padded panel's edges.
 *
 * The header's rule has to reach both sides of the card or it reads as an underline
 * under the first few words rather than as the line between a head and a body - and the
 * first column has to sit under the panel's own title rather than indented from it. The
 * panel's 16px padding and the cells' 16px padding are the same number for exactly this
 * reason.
 */
export const TABLE_FLUSH = `${TABLE_BASE} -mx-4 w-[calc(100%+2rem)]`;

/**
 * NO FILL. A tinted band across the top of a table separates by shading something in, on
 * a screen where a surface separates by being lighter than what is behind it and by
 * drawing one line. The header already reads as a header from its case, size and colour,
 * and the rule under it is a step stronger than the rules between the rows - which is the
 * actual signal that the head is not the first row.
 *
 * This note used to make its case by analogy to a grey page ground being obviously wrong.
 * The page is grey now and the argument survives it: the point was never that tint is bad,
 * it was that a table head is INSIDE a surface, and a second tint within one card is a
 * distinction the card has already drawn.
 */
export const TH =
  "border-b border-line px-4 py-2.5 text-left text-2xs font-semibold " +
  "tracking-[0.055em] whitespace-nowrap text-ink-faint uppercase";

export const TD = "border-b border-line-soft px-4 py-3 align-top";

/**
 * The qualifier that sits UNDER a number and never beside it.
 *
 * "quoted", "booked", "was $0.11" - the word that says what kind of figure this is.
 * Inline it collides with the number it qualifies and reads as one token: `$299plan`,
 * `$29197%`.
 *
 * `inline` IS THE EXCEPTION AND IT IS ONE ROW HIGH. The call log gives each call a single
 * line, so there is no "under" to sit in - and the collision the rule is about is a
 * qualifier butted against its number with nothing between them. A real gap and one step
 * down in size is the same distinction drawn along the line instead of across it. Nothing
 * with room for two lines should use it.
 */
export function Basis({ children, inline }: { children: ReactNode; inline?: boolean }) {
  return (
    <span
      className={cx(
        "text-2xs font-normal text-ink-faint lowercase",
        inline ? "ml-1" : "block",
      )}
    >
      {children}
    </span>
  );
}

/* --- settings ------------------------------------------------------------------------------ */

/*
 * THE SETTINGS PANES ARE CARDS AGAIN, and the note in `Form.tsx` that argued against boxing
 * a section is answered by what replaced the tabs: a pane is ONE section with a heading of
 * its own, so a card inside it is one container for one meaning rather than a card inside a
 * tab inside a page. These are the prototype's ("Settings v2", Claude Design) repeated
 * shapes, snapped to the scale: its 14px radius is `rounded-lg`, its 220px label column and
 * 22px/24px row padding are arbitrary values because `--spacing` is 0.28rem.
 */

/** A white card on the ground, the one surface a settings pane is built from. */
export function SettingCard({
  className,
  children,
  flush,
}: {
  className?: string;
  children: ReactNode;
  /** Clip the corners, for a card whose rows carry their own tint (a closed day, a strip). */
  flush?: boolean;
}) {
  return <div className={cx(SURFACE, flush && "overflow-hidden", className)}>{children}</div>;
}

/** The title row of a card: a name, an optional count, and whatever sits at the right. */
export function CardHead({
  title,
  count,
  note,
  aside,
}: {
  title: ReactNode;
  count?: ReactNode;
  /** One line under the title, for a card whose name does not explain itself. */
  note?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line-soft px-5 py-3.5">
      <div className="grid min-w-0 gap-0.5">
        <h3 className="flex items-baseline gap-2 text-base font-semibold text-ink">
          {title}
          {count}
        </h3>
        {note && <p className="text-xs text-ink-faint">{note}</p>}
      </div>
      {aside && <div className="flex flex-none items-center gap-2">{aside}</div>}
    </div>
  );
}

/**
 * One setting: its name and one line of help on the left, the control on the right.
 *
 * THE LABEL IS A `<label>` ONLY WHEN THERE IS ONE CONTROL TO POINT AT. A row of two choice
 * cards or a row of pills has several, and a label wrapping them would name the first one
 * after the row. `htmlFor` is passed by the caller that has an id; the rest get a heading.
 *
 * IT STACKS UNDER 720px, where 220px of label beside a text box leaves the box a sliver.
 */
export function SettingRow({
  label,
  help,
  htmlFor,
  first,
  children,
}: {
  label: string;
  help?: ReactNode;
  htmlFor?: string;
  /** No rule above it. The first row of a card sits directly under the card's edge. */
  first?: boolean;
  children: ReactNode;
}) {
  const Name = htmlFor ? "label" : "span";
  return (
    <div
      className={cx(
        "grid grid-cols-[220px_minmax(0,1fr)] gap-8 px-6 py-5.5",
        "max-[720px]:grid-cols-1 max-[720px]:gap-3 max-[720px]:px-4",
        !first && "border-t border-line-soft",
      )}
    >
      <div className="grid content-start gap-1">
        <Name htmlFor={htmlFor} className="text-sm font-medium text-ink">
          {label}
        </Name>
        {help && <span className="text-xs text-ink-faint">{help}</span>}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** `12 / 25`: a count against its cap, which turns alert at the cap. */
export function CountTag({ n, max, className }: { n: number; max?: number; className?: string }) {
  return (
    <span className={cx("nums font-mono text-xs font-normal", max !== undefined && n >= max ? "text-alert" : "text-ink-faint", className)}>
      {max === undefined ? n.toLocaleString() : `${n.toLocaleString()} / ${max.toLocaleString()}`}
    </span>
  );
}

/** "Our team only", on a pane only an internal reader is ever shown. */
export function TeamOnlyBadge() {
  return (
    <span className="rounded-full border border-accent-line bg-accent-soft px-2.5 py-0.5 text-2xs font-semibold text-accent">
      Our team only
    </span>
  );
}

/** A small grey tag on a nav item: a count, or "Soon". */
export function NavTag({ children }: { children: ReactNode }) {
  return <span className="nums rounded-full bg-line-soft px-2 py-px text-2xs font-semibold text-ink-faint">{children}</span>;
}

/**
 * One of two or three choices, drawn as a card with a ring.
 *
 * A REAL RADIO UNDER THE PAINT, for the reason `Toggle` gives about its checkbox: the
 * group's arrow keys, its single tab stop and its announcement are the platform's, and a
 * card that re-implemented them would get the second one wrong. The ring is the prototype's
 * - a 5px accent border when chosen, a grey hairline when not.
 */
export function ChoiceCard({
  name,
  value,
  checked,
  onChange,
  title,
  children,
  preview,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange: (value: string) => void;
  title: string;
  children?: ReactNode;
  /** A small picture of what choosing this looks like, above the title. */
  preview?: ReactNode;
}) {
  return (
    <label
      className={cx(
        "flex cursor-pointer flex-col gap-2.5 rounded-md border p-3.5 text-left transition-colors",
        checked ? "border-accent bg-accent-soft/50" : "border-line bg-paper hover:border-ink",
      )}
    >
      <input type="radio" className="peer sr-only" name={name} value={value} checked={checked} onChange={() => onChange(value)} />
      {preview}
      <span className="flex items-center gap-2 text-sm font-semibold text-ink">
        <span
          aria-hidden="true"
          className={cx("size-3.5 flex-none rounded-full", checked ? "border-[5px] border-accent" : "border-[1.5px] border-line-strong")}
        />
        {title}
      </span>
      {children && <span className="text-xs text-ink-soft">{children}</span>}
    </label>
  );
}

type Tone = "good" | "warm" | "neutral" | "alert";

const TONES: Record<Tone, string> = {
  good: "bg-good-soft text-good",
  warm: "bg-warm-soft text-warm",
  neutral: "bg-line-soft text-ink-soft",
  alert: "bg-alert-soft text-alert",
};

/** A state in two words and a dot: "Recorded", "Read out live". */
export function StatusPill({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span className={cx("badge-dot inline-flex h-6.5 flex-none items-center gap-2 rounded-full px-2.5 text-2xs font-semibold", TONES[tone])}>
      {children}
    </span>
  );
}
